import React, { useState } from "react";
import {
  RefinementList,
  ClearRefinements,
  useCurrentRefinements,
  useRefinementList,
} from "react-instantsearch";

// Refinement list with a search-as-you-type box. We filter the loaded facet
// values on the client instead of relying on Algolia's search-for-facet-values
// API, which requires the attribute to be configured as `searchable()` in the
// index settings.
const SearchableRefinementList = ({
  attribute,
  placeholder,
  sortBy,
  collapsedLimit = 50,
  fullLimit = 1000,
}) => {
  const { items, refine } = useRefinementList({
    attribute,
    sortBy,
    limit: fullLimit,
  });
  const [query, setQuery] = useState("");
  const [expanded, setExpanded] = useState(false);

  const normalizedQuery = query.trim().toLowerCase();
  const matched = normalizedQuery
    ? items.filter((item) =>
        item.label.toLowerCase().includes(normalizedQuery)
      )
    : items;

  const isFiltering = normalizedQuery.length > 0;
  const visibleItems =
    isFiltering || expanded ? matched : matched.slice(0, collapsedLimit);
  const canShowMore = !isFiltering && matched.length > collapsedLimit;

  return (
    <div className="refinement-list-root">
      <input
        type="search"
        className="refinement-list-searchbox"
        placeholder={placeholder}
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        autoComplete="off"
        spellCheck={false}
      />

      {visibleItems.length === 0 ? (
        <div className="refinement-list-no-results">No matches.</div>
      ) : (
        <ul className="refinement-list-list">
          {visibleItems.map((item) => (
            <li
              key={item.value}
              className={
                item.isRefined
                  ? "refinement-list-item refinement-list-item-selected"
                  : "refinement-list-item"
              }
            >
              <label className="refinement-list-label">
                <input
                  type="checkbox"
                  className="refinement-list-checkbox"
                  checked={item.isRefined}
                  onChange={() => refine(item.value)}
                />
                <span className="refinement-list-label-text">{item.label}</span>
                <span className="refinement-list-count">{item.count}</span>
              </label>
            </li>
          ))}
        </ul>
      )}

      {canShowMore && (
        <button
          onClick={() => setExpanded((value) => !value)}
          className="show-more-button"
        >
          {expanded ? "Show Less" : "Show More"}
        </button>
      )}
    </div>
  );
};

const AlgoliaRefineViewSidebar = () => {
  const [visibleOptions, setVisibleOptions] = useState("shop");

  const handleOptionClick = (option) => {
    setVisibleOptions(option);
  };

  // Custom component to display current refinements
  const CustomCurrentRefinements = () => {
    const { items, refine } = useCurrentRefinements();

    if (items.length === 0) {
      return null;
    }

    return (
      <div className="custom-current-refinements">
        {items.map((item) => (
          <div key={item.attribute} className="refinement-row">
            <span className="refinement-label">
              {item.attribute === "shop"
                ? "SITE"
                : item.attribute === "size"
                ? "SIZE"
                : item.attribute === "brand"
                ? "BRAND"
                : item.attribute === "keyword"
                ? "KEYWORD"
                : item.attribute.toUpperCase()}
              :{" "}
            </span>
            {item.refinements.map((refinement, index) => (
              <span key={refinement.value}>
                <span
                  className="refinement-value"
                  onClick={() => refine(refinement)}
                  style={{ cursor: "pointer" }}
                >
                  {refinement.label}
                </span>
                {index < item.refinements.length - 1 && ", "}
              </span>
            ))}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="filter-component-sidebar">
      <div className="sticky-header-sidebar">
        <div className="filter-header-sidebar"></div>
        <div className="clear-filters-row">
          <ClearRefinements
            classNames={{
              root: "clear-refinements-root",
              button: "clear-button-sidebar",
              disabledButton: "clear-button-sidebar-disabled",
            }}
            translations={{
              resetButtonText: "CLEAR",
            }}
          />
        </div>
        <div className="selected-filters-sidebar">
          <CustomCurrentRefinements />
        </div>
        <div className="filter-buttons-sidebar">
          <button
            onClick={() => handleOptionClick("shop")}
            className={visibleOptions === "shop" ? "selected" : ""}
          >
            SITES
          </button>
          <button
            onClick={() => handleOptionClick("size")}
            className={visibleOptions === "size" ? "selected" : ""}
          >
            SIZES
          </button>
          <button
            onClick={() => handleOptionClick("brand")}
            className={visibleOptions === "brand" ? "selected" : ""}
          >
            BRANDS
          </button>
          <button
            onClick={() => handleOptionClick("keyword")}
            className={visibleOptions === "keyword" ? "selected" : ""}
          >
            KEYWORDS
          </button>
        </div>
      </div>
      <div className="filter-options-sidebar">
        <div className="header-spacer-sidebar"></div>
        <div className="filter-items-sidebar">
          {/* All RefinementLists are always rendered - just show/hide with CSS */}
          <div
            style={{
              display: visibleOptions === "shop" ? "block" : "none",
            }}
          >
            <RefinementList
              attribute="shop"
              classNames={{
                root: "refinement-list-root",
                list: "refinement-list-list",
                item: "refinement-list-item",
                selectedItem: "refinement-list-item-selected",
                label: "refinement-list-label",
                checkbox: "refinement-list-checkbox",
                labelText: "refinement-list-label-text",
                count: "refinement-list-count",
              }}
              sortBy={["name:asc"]}
              showMore={false}
              limit={10}
            />
          </div>
          <div
            style={{
              display: visibleOptions === "size" ? "block" : "none",
            }}
          >
            <SearchableRefinementList
              attribute="size"
              placeholder="search sizes"
              sortBy={["name:asc"]}
            />
          </div>
          <div
            style={{
              display: visibleOptions === "brand" ? "block" : "none",
            }}
          >
            <SearchableRefinementList
              attribute="brand"
              placeholder="search brands"
              sortBy={["count:desc", "name:asc"]}
            />
          </div>
          <div
            style={{
              display: visibleOptions === "keyword" ? "block" : "none",
            }}
          >
            <SearchableRefinementList
              attribute="keyword"
              placeholder="search keywords"
              sortBy={["count:desc", "name:asc"]}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AlgoliaRefineViewSidebar;
